import { useEffect } from 'react';
import { FastingProtocol } from '@/types/fasting';

interface TimerInitializationProps {
  initialIsActive: boolean;
  initialStartTime: number | null;
  initialTimeElapsed: number;
  initialTotalPauseDuration: number;
  initRef: React.MutableRefObject<boolean>;
  setTimeElapsed: (value: number) => void;
  setIsActive: (value: boolean) => void;
  setStartTime: (value: number | null) => void;
  selectedProtocol: FastingProtocol;
  isLoaded?: boolean; // Add flag to wait for persistence loading
}

export const useTimerInitialization = ({
  initialIsActive,
  initialStartTime,
  initialTimeElapsed,
  initialTotalPauseDuration,
  initRef,
  setTimeElapsed,
  setIsActive,
  setStartTime,
  selectedProtocol,
  isLoaded = true // Default to true for backward compatibility
}: TimerInitializationProps) => {
  // Initialize timer state immediately on mount, but only after persistence is loaded
  useEffect(() => {
    if (initRef.current || !isLoaded) return;
    initRef.current = true;
    
    console.log("[TimerInit] Initializing timer with state:", {
      initialIsActive, 
      initialStartTime: initialStartTime ? new Date(initialStartTime).toLocaleString() : 'none',
      initialTimeElapsed,
      initialTotalPauseDuration
    });

    // If we have an active timer with a start time, restore it immediately
    if (initialIsActive && initialStartTime) {
      console.log("[TimerInit] Restoring active timer");
      
      // Calculate current elapsed time
      const now = Date.now();
      const pauseAdjustment = initialTotalPauseDuration || 0;
      const calculatedElapsed = Math.floor(((now - initialStartTime) - pauseAdjustment) / 1000);
      
      // Check if the fast should have completed by now
      const targetDuration = selectedProtocol.durationHours * 60 * 60;
      
      if (calculatedElapsed >= targetDuration) {
        // Fast should be complete - mark as inactive
        console.log("[TimerInit] Fast completed while away, marking as inactive");
        setIsActive(false);
        setStartTime(null);
        setTimeElapsed(targetDuration); // Set to target duration
      } else {
        // Fast is still ongoing
        const boundedElapsed = Math.max(0, calculatedElapsed);
        
        console.log("[TimerInit] Calculated current elapsed time:", {
          from: initialTimeElapsed,
          to: boundedElapsed,
          startTime: new Date(initialStartTime).toLocaleString(),
          pauseAdjustment
        });
        
        // Set all timer state synchronously to avoid race conditions
        setIsActive(true);
        setStartTime(initialStartTime);
        setTimeElapsed(boundedElapsed);
      }
      
      console.log("[TimerInit] Active timer restoration complete");
    } else if (!initialIsActive && initialStartTime === null) {
      // This is a clean state (no timer running)
      console.log("[TimerInit] Clean state - no timer running");
      setIsActive(false);
      setStartTime(null);
      setTimeElapsed(0);
    } else {
      // Handle edge cases
      console.log("[TimerInit] Handling edge case - inactive timer with saved data");
      setIsActive(initialIsActive);
      setStartTime(initialStartTime);
      setTimeElapsed(initialTimeElapsed);
    }
  }, [isLoaded]); // Add isLoaded as dependency
};
